import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { toast } from "sonner";
import { api, setUser, setToken } from "./lib/api";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RoleSelection from "./pages/RoleSelection";
import Dashboard from "./pages/Dashboard";
import Marketplace from "./pages/Marketplace";
import PostWaste from "./pages/PostWaste";
import FAQ from "./pages/FAQ";
import WasteDetail from "./pages/WasteDetail";
import ReuseBrowse from "./pages/ReuseBrowse";
import ReuseNew from "./pages/ReuseNew";
import ReuseDetail from "./pages/ReuseDetail";
import PriceGuide from "./pages/PriceGuide";
import Admin from "./pages/Admin";
import PaymentResult from "./pages/PaymentResult";
import AuthCallback from "./pages/AuthCallback";
import Policies from "./pages/Policies";
import Privacy from "./pages/Privacy";
import Settings from "./pages/Settings";
import InstallPrompt from "./components/InstallPrompt";
import CookieBanner from "./components/CookieBanner";
import ForceUpdateGate from "./components/ForceUpdateGate";

function App() {
  useEffect(() => {
    // Trigger seed once
    if (!localStorage.getItem("binshare_seeded")) {
      api.post("/seed")
        .then(() => localStorage.setItem("binshare_seeded", "1"))
        .catch(() => {});
    }
  }, []);

  // Native-only: listen for incoming `binshare://` deep links (Emergent
  // Google Auth handoff from Safari after OAuth). See AuthCallback.jsx
  // for the Safari-side that fires this URL.
  useEffect(() => {
    let listener;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { App: CapApp } = await import("@capacitor/app");
        listener = await CapApp.addListener("appUrlOpen", async (event) => {
          try {
            const url = new URL(event.url);
            // (1) Universal Link over https — Stripe redirect, deep-link
            //     into the app, marketing links etc. Just swap the current
            //     web view to the same path so BrowserRouter picks it up.
            if (url.protocol === "https:" && url.host === "app.binshare.co.uk") {
              const dest = `${url.pathname}${url.search}${url.hash}` || "/";
              // Close any in-app Safari view (Stripe leaves it open on iOS 26).
              try {
                const { Browser } = await import("@capacitor/browser");
                await Browser.close();
              } catch { /* noop */ }
              window.location.replace(dest);
              return;
            }
            // (2) OAuth handoff over the custom `binshare://` scheme.
            //     Left untouched from Phase 1 — silent hand-back into
            //     the app after Emergent Google auth.
            if (url.protocol !== "binshare:") return;
            if (!/^\/*auth\/*$/.test(url.pathname) && url.host !== "auth") return;
            const sessionId = url.searchParams.get("session_id");
            if (!sessionId) return;
            // Close the in-app Safari View if it's still up (fires and
            // forgets — Browser plugin no-ops if not open).
            try {
              const { Browser } = await import("@capacitor/browser");
              await Browser.close();
            } catch { /* noop */ }
            const { data } = await api.post("/auth/google-callback", { session_id: sessionId });
            setToken(data.token);
            setUser(data.user);
            toast.success(`Welcome${data.user?.name ? `, ${data.user.name}` : ""}!`);
            const next = data.user?.needs_role_selection ? "/onboarding/role" : "/dashboard";
            // Full nav — App.js sits above <BrowserRouter> so useNavigate
            // isn't available here; window.location gives us a clean state.
            window.location.replace(next);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Google sign-in failed");
            window.location.replace("/login");
          }
        });
      } catch {
        // Capacitor not available (running as pure web) — nothing to do.
      }
    })();
    return () => {
      try { listener && listener.remove(); } catch { /* noop */ }
    };
  }, []);

  return (
    <div className="App">
      <ForceUpdateGate>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <InstallPrompt />
          <CookieBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding/role" element={<RoleSelection />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/reuse" element={<ReuseBrowse />} />
            <Route path="/reuse/new" element={<ReuseNew />} />
            <Route path="/reuse/:id" element={<ReuseDetail />} />
            <Route path="/price-guide" element={<PriceGuide />} />
            <Route path="/post-waste" element={<PostWaste />} />
            <Route path="/waste/:id" element={<WasteDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/payment-success" element={<PaymentResult outcome="success" />} />
            <Route path="/payment-cancel" element={<PaymentResult outcome="cancel" />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/terms" element={<Policies />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/settings" element={<Settings />} />
            {/* Unknown URLs (incl. the retired /blog pages) go home rather
                than rendering an empty shell. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ForceUpdateGate>
    </div>
  );
}

export default App;
